/**
 * Resolve the display value of a (possibly translatable) field for a given
 * locale: active-locale sibling wins, else the base column — blank string
 * siblings are skipped in favor of the base (a saved-but-empty translation
 * must not blank the display).
 *
 * Two-tier by design, matching the backend's `_resolve_localized`
 * (`core/runtime/reference_display.py`): `field__locale` -> base. A prior
 * version added a THIRD tier (a hardcoded `defaultLocale = 'en'` fallback
 * before the base column) that doesn't exist server-side and was wrong for
 * any tenant whose default language isn't English (finding M1) — dropped
 * rather than threading the real tenant default through every list-cell
 * formatter call site for a fallback the base column already covers.
 *
 * Used wherever App-mode read paths need one string out of a translatable
 * field's base + `<field>__<locale>` siblings (list cells, print, etc.) —
 * the record-form editing popover itself binds directly to `values[field]`
 * / `values[field__code]` (see `TranslatableTextField`), not through this.
 */
export function resolveFieldValue(
  row: Record<string, unknown> | undefined | null,
  field: string,
  locale: string,
): string {
  if (!row || !field) return ''
  const sibling = row[`${field}__${locale}`]
  if (typeof sibling === 'string' && sibling.trim() !== '') return sibling
  const base = row[field]
  return base == null ? '' : String(base)
}
